// src/pages/CaseHistoryPage/SectionRenderer.js
import React from 'react';
import MilestoneGrid from './fields/MilestoneGrid';
import PhonemeInventory from './fields/PhonemeInventory';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `36` -> "3 साल 0 महीने".
 *
 * Every age in this form is in months, and nothing on the screen said so, so the
 * same box was being answered in years by one person and months by another. The
 * conversion under the box is what actually settles it: someone who types 3
 * meaning years reads back "0 साल 3 महीने" and corrects themselves. */
const monthsInWords = (months, lang) => {
    const years = Math.floor(months / 12);
    const rest = months % 12;
    const hi = `${years} साल ${rest} महीने`;
    const en = `${years}y ${rest}m`;
    if (lang === 'hi') return hi;
    if (lang === 'en') return `${years} years ${rest} months`;
    return `${hi} · ${en}`;
};

/** The response names the child's own fields `child_*`. Splitting the schema's
 * "child.date_of_birth" on the dot looked up `date_of_birth`, which is not there —
 * which is why all four prefill rows in Section 1 printed an em dash. */
const PREFILL_FIELDS = {
    'child.name': 'child_name',
    'child.date_of_birth': 'child_date_of_birth',
    'child.gender': 'child_gender',
    'child.disabilities': 'child_disabilities',
};

const prefillValue = (question, child, lang) => {
    if (!child) return null;
    const field = PREFILL_FIELDS[question.source];
    const raw = field ? child[field] : null;
    if (raw === null || raw === undefined || raw === '') return null;

    if (field === 'child_date_of_birth') {
        // Formatted by hand: toLocaleDateString follows the browser's locale, and
        // the examiner should not be shown a different order on a different machine.
        const d = new Date(`${raw}T00:00:00`);
        if (Number.isNaN(d.getTime())) return String(raw);
        const shown = `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
        // The age beside it is the thing actually being checked against the child.
        return child.child_age_months === null || child.child_age_months === undefined
            ? shown
            : `${shown} · ${monthsInWords(child.child_age_months, lang)}`;
    }
    if (Array.isArray(raw)) return raw.length ? raw.join(', ') : null;
    return String(raw);
};

const bothLines = (text, lang) => {
    if (lang === 'hi') return <span className="hi">{text.hi}</span>;
    if (lang === 'en') return <span>{text.en}</span>;
    return (
        <>
            <span className="hi">{text.hi}</span>
            <span className="ch-en">{text.en}</span>
        </>
    );
};

/** One question. The `switch` is the whole renderer — types come from the schema. */
function Question({ question, value, onChange, onBlur, lang, disabled, prefill }) {
    const common = { disabled, onBlur };

    switch (question.type) {
        case 'prefill':
            return <div className="ch-prefill">{prefill ?? <span className="muted">—</span>}</div>;

        case 'text':
        case 'date':
            return (
                <input
                    className="ch-input"
                    type={question.type === 'date' ? 'date' : 'text'}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    {...common}
                />
            );

        case 'textarea':
            return (
                <textarea
                    className="ch-input ch-textarea"
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    {...common}
                />
            );

        case 'number':
        case 'months': {
            const box = (
                <input
                    className="ch-input ch-number"
                    type="number" inputMode="numeric"
                    min={question.min} max={question.max} step={question.step}
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
                    {...common}
                />
            );
            if (question.type !== 'months') return box;
            const typed = typeof value === 'number' && Number.isFinite(value) && value >= 0;
            return (
                <div className="ch-months">
                    <div className="ch-months-row">
                        {box}
                        <span className="ch-unit">
                            {lang === 'en' ? 'months' : lang === 'hi' ? 'महीने' : 'महीने · months'}
                        </span>
                    </div>
                    {typed && <div className="ch-months-conv">= {monthsInWords(value, lang)}</div>}
                </div>
            );
        }

        case 'boolean':
            return (
                <div className="ch-choices">
                    {[[true, 'हाँ', 'Yes'], [false, 'नहीं', 'No']].map(([v, hi, en]) => (
                        <button
                            type="button" key={String(v)}
                            className={`ch-choice ${value === v ? 'on' : ''}`}
                            onClick={() => { onChange(v); onBlur && onBlur(); }}
                            disabled={disabled}
                            aria-pressed={value === v}
                        >
                            {lang === 'en' ? en : lang === 'hi' ? hi : `${hi} / ${en}`}
                        </button>
                    ))}
                </div>
            );

        case 'single_choice':
            return (
                <div className="ch-choices">
                    {(question.options || []).map((o) => (
                        <button
                            type="button" key={o.key}
                            className={`ch-choice ${value === o.key ? 'on' : ''}`}
                            onClick={() => { onChange(o.key); onBlur && onBlur(); }}
                            disabled={disabled}
                            aria-pressed={value === o.key}
                        >
                            {lang === 'en' ? o.en : <span className="hi">{o.hi}</span>}
                        </button>
                    ))}
                </div>
            );

        case 'multi_choice': {
            const selected = Array.isArray(value) ? value : [];
            return (
                <div className="ch-choices">
                    {(question.options || []).map((o) => (
                        <button
                            type="button" key={o.key}
                            className={`ch-choice ${selected.includes(o.key) ? 'on' : ''}`}
                            onClick={() => {
                                onChange(selected.includes(o.key)
                                    ? selected.filter((k) => k !== o.key)
                                    : [...selected, o.key]);
                                onBlur && onBlur();
                            }}
                            disabled={disabled}
                            aria-pressed={selected.includes(o.key)}
                        >
                            {lang === 'en' ? o.en : <span className="hi">{o.hi}</span>}
                        </button>
                    ))}
                </div>
            );
        }

        case 'grid':
            return <MilestoneGrid question={question} value={value || {}} onChange={onChange} onBlur={onBlur} lang={lang} disabled={disabled} />;

        case 'phoneme_inventory':
            return <PhonemeInventory value={value} onChange={onChange} onBlur={onBlur} lang={lang} disabled={disabled} />;

        default:
            return <div className="muted">Unsupported question type: {question.type}</div>;
    }
}

export default function SectionRenderer({ section, answers, onAnswer, onBlur, lang, disabled, child }) {
    if (!section) return null;

    if (section.owner === 'derived') {
        return (
            <div className="ch-note blue">
                {section.hint ? (lang === 'en' ? section.hint.en : section.hint.hi) : ''}
                <div style={{ marginTop: 6 }}>Not recorded yet.</div>
            </div>
        );
    }
    if (section.owner === 'therapist') {
        return (
            <div className="ch-note amber">
                🔒 {lang === 'en' ? 'Filled in by the therapist, after the recordings.'
                    : 'यह थेरेपिस्ट भरते हैं — रिकॉर्डिंग के बाद।'}
            </div>
        );
    }

    // A question with `depends_on` only appears once its parent is answered yes.
    //
    // `depends_value` narrows that to one option — which is what an "अन्य / other"
    // follow-up needs: it belongs to that choice, not to the question. A multi-choice
    // parent holds an array, and an array satisfied neither branch below, so a
    // follow-up hung off one would never have appeared at all.
    const visible = (question) => {
        if (!question.depends_on) return true;
        const parent = answers[question.depends_on];
        if (question.depends_value !== undefined) {
            return Array.isArray(parent)
                ? parent.includes(question.depends_value)
                : parent === question.depends_value;
        }
        if (Array.isArray(parent)) return parent.length > 0;
        return parent === true || (typeof parent === 'string' && parent && parent !== 'no' && parent !== 'unknown');
    };

    return (
        <div className="ch-questions">
            {section.hint && (
                <div className="ch-note amber">
                    {lang === 'en' ? section.hint.en : <span className="hi">{section.hint.hi}</span>}
                </div>
            )}
            {section.questions.filter(visible).map((question) => (
                <div className="ch-q" key={question.key}>
                    <label className="ch-label">
                        {bothLines(question.label, lang)}
                        {question.required && <span className="ch-req"> *</span>}
                    </label>
                    <Question
                        question={question}
                        value={answers[question.key]}
                        onChange={(v) => onAnswer(question.key, v)}
                        onBlur={onBlur}
                        lang={lang}
                        disabled={disabled}
                        prefill={prefillValue(question, child, lang)}
                    />
                </div>
            ))}
        </div>
    );
}
